import {
    AsyncLoadable,
    CollectionBase,
    FocusableProps,
    HelpTextProps,
    InputBase,
    KeyboardDelegate,
    LabelableProps,
    MultipleSelection,
    PressEvent,
    TextInputBase,
    Validation,
} from '@react-types/shared';
import { FocusEvent, HTMLAttributes, RefObject, useMemo } from 'react';
import {
    chain,
    mergeProps,
    useCollator,
    useId,
    useMenuTrigger,
    useField,
    AriaButtonProps,
    AriaListBoxOptions,
} from 'react-aria';
import { ListKeyboardDelegate, useTypeSelect } from '@react-aria/selection';

import { MultiSelectState } from './useMultiSelectState';

export interface MultiSelectProps<T>
    extends CollectionBase<T>,
        AsyncLoadable,
        Omit<InputBase, 'isReadOnly'>,
        Validation<T>,
        HelpTextProps,
        LabelableProps,
        TextInputBase,
        MultipleSelection,
        FocusableProps {
    /** Sets the open state of the menu. */
    isOpen?: boolean;
    /** Sets the default open state of the menu. */
    defaultOpen?: boolean;
    /** Method that is called when the open state of the menu changes. */
    onOpenChange?: (isOpen: boolean) => void;
    /**
     * Whether the menu should automatically flip direction when space is limited.
     * @default true
     */
    shouldFlip?: boolean;
    /**
     * Describes the type of autocomplete functionality the input should provide if any. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefautocomplete).
     */
    autoComplete?: string;
    /**
     * The name of the input, used when submitting an HTML form.
     */
    name?: string;
    /**
     * An optional keyboard delegate implementation for type to select,
     * to override the default.
     */
    keyboardDelegate?: KeyboardDelegate;
}

export interface MultiSelectAria<T extends object> {
    /** Props for the label element. */
    labelProps: HTMLAttributes<HTMLElement>;

    /** Props for the popup trigger element. */
    triggerProps: AriaButtonProps;

    /** Props for the element representing the selected value. */
    valueProps: HTMLAttributes<HTMLElement>;

    /** Props for the popup. */
    menuProps: AriaListBoxOptions<T>;
}

export function useMultiSelect<T extends object>(
    props: MultiSelectProps<T>,
    state: MultiSelectState<T>,
    ref: RefObject<HTMLElement>,
): MultiSelectAria<T> {
    const { isDisabled } = props;

    const collator = useCollator({ usage: 'search', sensitivity: 'base' });
    const delegate = useMemo(
        // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        () => new ListKeyboardDelegate(state.collection, state.disabledKeys, null as never, collator),
        [state.collection, state.disabledKeys, collator],
    );

    const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
        {
            isDisabled,
            type: 'listbox',
        },
        state,
        ref,
    );

    const triggerOnKeyDown = (e: KeyboardEvent) => {
        // Select items when trigger has focus - imitating default `<select>` behaviour.
        // In multi selection mode it does not make sense.
        if (state.selectionManager.selectionMode === 'single') {
            switch (e.key) {
                case 'ArrowLeft': {
                    // prevent scrolling containers
                    e.preventDefault();

                    const selectedValue = state.selectionManager.selectedKeys.values().next().value;
                    const key =
                        selectedValue !== undefined ? delegate.getKeyAbove(selectedValue) : delegate.getFirstKey();

                    if (key) {
                        state.selectionManager.setSelectedKeys([key]);
                    }
                    break;
                }
                case 'ArrowRight': {
                    // prevent scrolling containers
                    e.preventDefault();

                    const selectedValue = state.selectionManager.selectedKeys.values().next().value;
                    const key =
                        selectedValue !== undefined ? delegate.getKeyBelow(selectedValue) : delegate.getFirstKey();

                    if (key) {
                        state.selectionManager.setSelectedKeys([key]);
                    }
                    break;
                }

                // no default
            }
        }
    };

    // Typeahead functionality - imitating default `<select>` behaviour.
    const { typeSelectProps } = useTypeSelect({
        keyboardDelegate: delegate,
        selectionManager: state.selectionManager,
        onTypeSelect(key) {
            state.selectionManager.setSelectedKeys([key]);
        },
    });

    const { labelProps, fieldProps } = useField({
        ...props,
        labelElementType: 'span',
    });

    // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-288723
    typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;

    // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-288723
    delete typeSelectProps.onKeyDownCapture;

    const triggerProps = mergeProps(typeSelectProps, menuTriggerProps, fieldProps);

    const valueId = useId();

    return {
        labelProps: {
            ...labelProps,
            onClick: () => {
                if (!props.isDisabled) {
                    ref.current?.focus();
                }
            },
        },
        triggerProps: mergeProps(triggerProps, {
            onKeyDown: chain(triggerProps.onKeyDown, triggerOnKeyDown, props.onKeyDown),
            onKeyUp: props.onKeyUp,
            // react-aria отрабатывает только открытие селекта, закрытие обрабатываем сами
            onPressStart: (event: PressEvent) => {
                if (event.pointerType !== 'touch' && event.pointerType !== 'keyboard' && state.isOpen) {
                    state.close();
                }
            },
            'aria-labelledby': [
                triggerProps['aria-labelledby'],
                triggerProps['aria-label'] && !triggerProps['aria-labelledby'] ? triggerProps.id : null,
                valueId,
            ]
                .filter(Boolean)
                .join(' '),
            onFocus(event: FocusEvent) {
                if (state.isFocused) {
                    return;
                }

                props.onFocus?.(event);

                state.setFocused(true);
            },
            onBlur(event: FocusEvent) {
                if (state.isOpen) {
                    return;
                }

                props.onBlur?.(event);

                state.setFocused(false);
            },
        }),
        valueProps: {
            id: valueId,
        },
        menuProps: {
            ...menuProps,
            disallowEmptySelection: true,
            autoFocus: false,
            shouldFocusOnHover: true,
            onBlur: event => {
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                if (event.currentTarget.contains(event.relatedTarget as Node)) {
                    return;
                }

                if (props.onBlur) {
                    props.onBlur(event);
                }
                state.setFocused(false);
            },
            'aria-labelledby': [
                fieldProps['aria-labelledby'],
                triggerProps['aria-label'] && !fieldProps['aria-labelledby'] ? triggerProps.id : null,
            ]
                .filter(Boolean)
                .join(' '),
        },
    };
}
